import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BG_Image from '../assets/bg_ocean2.png'
import BloomImage from '../assets/bloom.jpg'
import DSC1Image from '../assets/DSC_0059.JPG'
import DSC2Image from '../assets/DSC_0086.JPG'
import { useTranslation } from "react-i18next";
import NavigationGuide from './imageCarousel';
import { MdOutlineMonitorHeart } from "react-icons/md";
import { TbMapPins } from "react-icons/tb";
import { BsGraphUpArrow } from "react-icons/bs";
import Navbar from "../components/navbar";
import Footer from '../components/footer.js';
import TopAffectedRegionsBar from '../components/TopAffectedRegionsBar.js';
import MonthlyBarChart from '../components/MonthlyOccurencesStats.js';
import { DataContext } from '../components/DataContext';
import ScrollReveal from '../components/Animation/ScrollReveal.js';

export default function Home() {
  const { t } = useTranslation();
  const { measurements, loading, locations } = React.useContext(DataContext);

  // Build a map from location id to name for city name lookup
  const locNameMap = React.useMemo(() => {
    const map = new Map();
    (locations || []).forEach(loc => {
      const id = loc.id ?? loc.locationId ?? loc.location_id ?? loc.code ?? loc.slug;
      const name = loc.name ?? loc.region ?? loc.title ?? `Location ${id ?? ""}`;
      if (id != null) map.set(String(id), String(name));
      if (loc.name && !map.has(loc.name)) map.set(loc.name, loc.name);
    });
    return map;
  }, [locations]);
  const [currentCard, setCurrentCard] = useState(0);

  // Content for the slider based on client's text
  const slides = [
    {
      title: t("shellsafe.challenge"),
      content: t("shellsafe.challenge_description"),
      image: BloomImage,
      caption: t("shellsafe.algal_blooms")
    },
    {
      title: t("shellsafe.solution"),
      content: t("shellsafe.solution_description"),
      image: DSC1Image,
      caption: t("shellsafe.solution_caption")
    },
    {
      title: t("shellsafe.performance"),
      content: t("shellsafe.performance_description"),
      image: DSC2Image,
      caption: t("shellsafe.performance_caption")
    }
  ];

  const nextCard = () => {
    setCurrentCard((prev) => (prev + 1) % slides.length);
  };

  const prevCard = () => {
    setCurrentCard((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-blue-50 text-gray-800">
      <Navbar />
      <section className="mt-5 bg-cover bg-center h-[500px] flex items-center justify-center text-white" style={{ backgroundImage: `url(${BG_Image})` }}>
        <div className="text-center pt-20 p-6 rounded">
          <h1 className="text-4xl font-bold mb-2">{t("home.hero_title")}</h1>
          <p className="text-lg mb-8">{t("home.hero_subtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/login" 
              className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              {t("home.hero_buttons.signup")}
            </Link>
            <Link 
              to="/map" 
              className="px-8 py-3 bg-transparent border-2 border-white hover:bg-white hover:text-teal-600 text-white rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {t("home.hero_buttons.view_map")}
            </Link>
          </div>
        </div>
      </section>

      {/* Info Panels */}
      <section className="max-w-6xl mx-auto px-4 pt-12 pb-6 grid md:grid-cols-3 gap-8">
        <div className="bg-white shadow-lg rounded-lg p-6 text-center group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
              <div className="text-4xl text-teal-600 transform transition-transform group-hover:scale-110 duration-300">
                <MdOutlineMonitorHeart />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t("home.features.monitoring.title")}</h2>
            <p className="text-gray-600">{t("home.features.monitoring.description")}</p>
            <div className="w-12 h-1 bg-teal-500 rounded-full mt-4 transition-all duration-300 group-hover:w-20"></div>
          </div>
        </div>
        <div className="bg-white shadow-lg rounded-lg p-6 text-center group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
              <div className="text-4xl text-teal-600 transform transition-transform group-hover:scale-110 duration-300">
                <TbMapPins />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t("home.features.heatmaps.title")}</h2>
            <p className="text-gray-600">{t("home.features.heatmaps.description")}</p>
            <div className="w-12 h-1 bg-teal-500 rounded-full mt-4 transition-all duration-300 group-hover:w-20"></div>
          </div>
        </div>
        <div className="bg-white shadow-lg rounded-lg p-6 text-center group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
              <div className="text-4xl text-teal-600 transform transition-transform group-hover:scale-110 duration-300">
                <BsGraphUpArrow />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t("home.features.analytics.title")}</h2>
            <p className="text-gray-600">{t("home.features.analytics.description")}</p>
            <div className="w-12 h-1 bg-teal-500 rounded-full mt-4 transition-all duration-300 group-hover:w-20"></div>
          </div>
        </div>
      </section>

      {/* Key Information Section with Client's Content */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl text-center font-bold text-gray-800 mb-8">
            {t("shellsafe.title")}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Left Info Column */}
            <div className="relative bg-white rounded-xl shadow-lg p-8 min-h-[200px] flex flex-col justify-between transition-all duration-300 transform hover:shadow-xl">
              <h3 className="text-xl font-bold text-teal-700 mb-4">{slides[currentCard].title}</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {slides[currentCard].content}
              </p>
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-teal-600 h-full transition-all duration-300"
                  style={{ width: `${((currentCard + 1) / slides.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Right Images Column */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full">
              <div className="relative w-full h-full">
                <img 
                  src={slides[currentCard].image} 
                  alt={slides[currentCard].title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-white text-sm">
                    {slides[currentCard].caption}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-between items-center">
            <button
              onClick={prevCard}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors duration-300 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t("shellsafe.navigation.previous")}
            </button>
            <div className="flex items-center gap-1">
              {slides.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-2 w-2 rounded-full ${
                    currentCard === idx ? 'bg-teal-600 w-4' : 'bg-gray-300'
                  } transition-all duration-300`}
                  onClick={() => setCurrentCard(idx)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
              <span className="ml-3 text-gray-600">
                {currentCard + 1} {t("shellsafe.navigation.of")} {slides.length}
              </span>
            </div>
            <button
              onClick={nextCard}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors duration-300 flex items-center gap-2"
            >
              {t("shellsafe.navigation.next")}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Shellfish Pollution Statistics */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl text-center font-bold text-gray-800 mb-10">
            {t("statistics.title")}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Stat 1 */}
            <ScrollReveal>
              <div className="bg-blue-50 rounded-lg p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal-600 mb-2">{t("statistics.economic_impact.value")}</div>
                <h3 className="text-lg font-semibold mb-2">{t("statistics.economic_impact.title")}</h3>
                <p className="text-gray-600">{t("statistics.economic_impact.description")}</p>
              </div>
            </ScrollReveal>
            
            {/* Stat 2 */}
            <ScrollReveal delay={80}>
              <div className="bg-blue-50 rounded-lg p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal-600 mb-2">{t("statistics.annual_losses.value")}</div>
                <h3 className="text-lg font-semibold mb-2">{t("statistics.annual_losses.title")}</h3>
                <p className="text-gray-600">{t("statistics.annual_losses.description")}</p>
              </div>
            </ScrollReveal>
            
            {/* Stat 3 */}
            <ScrollReveal delay={160}>
              <div className="bg-blue-50 rounded-lg p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal-600 mb-2">{t("statistics.response_time.value")}</div>
                <h3 className="text-lg font-semibold mb-2">{t("statistics.response_time.title")}</h3>
                <p className="text-gray-600">{t("statistics.response_time.description")}</p>
              </div>
            </ScrollReveal>
            
            {/* Stat 4 */}
            <ScrollReveal delay={240}>
              <div className="bg-blue-50 rounded-lg p-6 text-center shadow-md">
                <div className="text-4xl font-bold text-teal-600 mb-2">{t("statistics.prediction_accuracy.value")}</div>
                <h3 className="text-lg font-semibold mb-2">{t("statistics.prediction_accuracy.title")}</h3>
                <p className="text-gray-600">{t("statistics.prediction_accuracy.description")}</p>
              </div>
            </ScrollReveal>
          </div>
          
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Monthly HAB Occurrences */}
            <ScrollReveal>
              <MonthlyBarChart
                data={measurements}
                loading={loading}
                title={t("statistics.monthly_hab.title")}
                valueAccessor={m => {
                  // Accepts various date and value fields
                  const rawDate = m?.date ?? m?.sample_date ?? m?.sampleDate ?? m?.measured_at ?? m?.measuredAt;
                  if (typeof rawDate !== "string") return null;
                  const mdate = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                  if (!mdate) return null;
                  const y = Number(mdate[1]), mo = Number(mdate[2]) - 1;
                  if (y < 2020 || y > 2025) return null;
                  // Accept various DSP fields
                  const dsp = Number(m?.dsp ?? m?.DSP ?? m?.valueDSP ?? m?.dsp_value ?? m?.value ?? null);
                  if (!(Number.isFinite(dsp) && dsp > 160)) return null;
                  return { month: mo, count: 1 };
                }}
              />
            </ScrollReveal>

            <ScrollReveal delay={120}>
              <TopAffectedRegionsBar
                data={measurements}
                loading={loading}
                title={t("statistics.affected_areas.title")}
                valueAccessor={m => {
                  // Accepts various date and value fields
                  const rawDate = m?.date ?? m?.sample_date ?? m?.sampleDate ?? m?.measured_at ?? m?.measuredAt;
                  const mdate = typeof rawDate === "string" ? rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
                  if (!mdate) return null;
                  const y = Number(mdate[1]);
                  if (y < 2020 || y > 2025) return null;
                  // Accept various DSP fields
                  const dsp = Number(m?.dsp ?? m?.DSP ?? m?.valueDSP ?? m?.dsp_value ?? m?.value ?? null);
                  if (!(Number.isFinite(dsp) && dsp > 160)) return null;
                  // Accept various location keys and names
                  const key = String(
                    m?.locationId ?? m?.location_id ?? m?.location?.id ?? m?.site_id ?? m?.station_id ?? m?.location_code ?? m?.location ?? m?.locationName ?? "unknown"
                  );
                  const name = locNameMap.get(key) ?? m?.locationName ?? m?.location?.name ?? m?.region ?? m?.site_name ?? key;
                  return { key, name, count: 1 };
                }}
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <NavigationGuide />
      <Footer/>
    </div>
  );
}

